import { Typography } from "@/components/common/Typography/Typography"
import {
  DocumentTagBadge,
  type DocumentTagEntry,
} from "@/components/feature/KnowledgebaseFilters/DocumentTagCategoryMenu"

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type {
  KnowledgeBaseCategory,
  KnowledgeBaseTag,
} from "@/types/knowledgebaseCategories"
import { classNames } from "@/utils/classNames"
import { Check, Loader2, Plus } from "lucide-react"
import * as React from "react"
import { createPortal } from "react-dom"
import { getLocalizedLabel } from "../Modals/TagDocumentDialog/TagDocumentShared"

interface InlineCategoryTagCellEditorProps {
  category: KnowledgeBaseCategory
  selectedEntries: DocumentTagEntry[]
  selectedTagIds: string[]
  isActive: boolean
  onActivate: () => void
  onSelectionChange: (values: string[]) => void
  onRemoveTag: (entry: DocumentTagEntry) => void
  onCreateTag: (
    category: KnowledgeBaseCategory,
    tagName: string,
  ) => Promise<KnowledgeBaseTag | undefined>
  isCreatingTag: boolean
  portalOwnerId: string
  onClose: () => void
}

export const InlineCategoryTagCellEditor: React.FC<
  InlineCategoryTagCellEditorProps
> = ({
  category,
  selectedEntries,
  selectedTagIds,
  isActive,
  onActivate,
  onSelectionChange,
  onRemoveTag,
  onCreateTag,
  isCreatingTag,
  portalOwnerId,
  onClose,
}) => {
  const { currentLanguage, translate } = useTranslation()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [query, setQuery] = React.useState("")
  const [highlightedIndex, setHighlightedIndex] = React.useState(0)
  const [createdTags, setCreatedTags] = React.useState<KnowledgeBaseTag[]>([])
  const [dropdownStyle, setDropdownStyle] = React.useState<{
    left: number
    top: number
    width: number
  } | null>(null)

  React.useEffect(() => {
    if (!isActive) {
      setQuery("")
      setHighlightedIndex(0)
      return
    }

    inputRef.current?.focus()
  }, [isActive])

  const selectedTagIdSet = React.useMemo(
    () => new Set(selectedTagIds),
    [selectedTagIds],
  )

  const normalizedQuery = query.trim().toLocaleLowerCase()
  const trimmedQuery = query.trim()

  const availableTags = React.useMemo(() => {
    const tagsById = new Map(category.tags.map((tag) => [tag.id, tag]))
    for (const tag of createdTags) {
      tagsById.set(tag.id, tag)
    }
    return Array.from(tagsById.values())
  }, [category.tags, createdTags])

  const filteredTags = React.useMemo(
    () =>
      availableTags.filter((tag) => {
        if (!tag.isActive) return false
        if (!normalizedQuery) return true

        const localizedLabel = getLocalizedLabel(
          tag.displayNameEn,
          tag.displayNameDe,
          currentLanguage,
        ).toLocaleLowerCase()

        return (
          localizedLabel.includes(normalizedQuery) ||
          tag.id.toLocaleLowerCase().includes(normalizedQuery)
        )
      }),
    [availableTags, currentLanguage, normalizedQuery],
  )

  const canCreateTag =
    trimmedQuery.length > 0 &&
    !availableTags.some(
      (tag) =>
        tag.isActive &&
        getLocalizedLabel(tag.displayNameEn, tag.displayNameDe, currentLanguage)
          .trim()
          .toLocaleLowerCase() === normalizedQuery,
    )

  React.useEffect(() => {
    setCreatedTags((current) =>
      current.filter(
        (tag) => !category.tags.some((nextTag) => nextTag.id === tag.id),
      ),
    )
  }, [category.tags])

  React.useEffect(() => {
    setHighlightedIndex(0)
  }, [normalizedQuery, filteredTags.length, canCreateTag])

  const optionCount = filteredTags.length + (canCreateTag ? 1 : 0)

  React.useLayoutEffect(() => {
    if (!isActive || optionCount === 0) {
      setDropdownStyle(null)
      return
    }

    const updatePosition = () => {
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect) return

      setDropdownStyle({
        left: rect.left,
        top: rect.bottom + 4,
        width: rect.width,
      })
    }

    updatePosition()
    window.addEventListener("resize", updatePosition)
    window.addEventListener("scroll", updatePosition, true)

    return () => {
      window.removeEventListener("resize", updatePosition)
      window.removeEventListener("scroll", updatePosition, true)
    }
  }, [isActive, optionCount])

  const toggleTag = React.useCallback(
    (tagId: string) => {
      const nextSelection = new Set(selectedTagIdSet)
      if (nextSelection.has(tagId)) {
        nextSelection.delete(tagId)
      } else {
        nextSelection.add(tagId)
      }
      onSelectionChange(Array.from(nextSelection))
    },
    [onSelectionChange, selectedTagIdSet],
  )

  const handleSelectTag = React.useCallback(
    (tagId: string) => {
      toggleTag(tagId)
      setQuery("")
      setHighlightedIndex(0)
    },
    [toggleTag],
  )

  const handleCreateTag = React.useCallback(async () => {
    if (!canCreateTag || isCreatingTag) return
    const createdTag = await onCreateTag(category, trimmedQuery)
    if (createdTag) {
      setCreatedTags((current) => [...current, createdTag])
      toggleTag(createdTag.id)
      setQuery("")
      setHighlightedIndex(0)
    }
  }, [
    canCreateTag,
    category,
    isCreatingTag,
    onCreateTag,
    toggleTag,
    trimmedQuery,
  ])

  const dropdown =
    isActive && optionCount > 0 && dropdownStyle
      ? createPortal(
          <div
            data-inline-tag-editor-owner={portalOwnerId}
            className="fixed z-60 rounded-md border border-grey4 bg-grey1 shadow-md"
            style={{
              left: dropdownStyle.left,
              top: dropdownStyle.top,
              width: dropdownStyle.width,
            }}
          >
            <div className="max-h-56 overflow-auto py-1">
              {filteredTags.map((tag, index) => {
                const localizedLabel = getLocalizedLabel(
                  tag.displayNameEn,
                  tag.displayNameDe,
                  currentLanguage,
                )
                const isSelected = selectedTagIdSet.has(tag.id)
                const isHighlighted = highlightedIndex === index

                return (
                  <button
                    key={tag.id}
                    type="button"
                    className={classNames(
                      "flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-grey6 transition-colors hover:bg-grey2",
                      isHighlighted ? "bg-grey2" : "",
                      isSelected ? "bg-yellow1/10" : "",
                    )}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => handleSelectTag(tag.id)}
                  >
                    <div
                      aria-hidden="true"
                      className={classNames(
                        "grid h-4 w-4 shrink-0 place-content-center rounded-sm border border-primary shadow-sm",
                        isSelected ? "bg-primary text-primary-foreground" : "",
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </div>
                    <span className="truncate">{localizedLabel}</span>
                  </button>
                )
              })}

              {canCreateTag && (
                <button
                  type="button"
                  className={classNames(
                    "flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-grey6 transition-colors hover:bg-grey2",
                    highlightedIndex === filteredTags.length ? "bg-grey2" : "",
                    isCreatingTag ? "cursor-wait" : "",
                  )}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setHighlightedIndex(filteredTags.length)}
                  disabled={isCreatingTag}
                  onClick={() => void handleCreateTag()}
                >
                  {isCreatingTag ? (
                    <Loader2 className="size-4 shrink-0 animate-spin" />
                  ) : (
                    <Plus className="size-4 shrink-0" />
                  )}
                  <span className="truncate">
                    {translate("KNOWLEDGEBASE/TAG_CREATE_ACTION", {
                      tag: trimmedQuery,
                    })}
                  </span>
                </button>
              )}
            </div>
          </div>,
          document.body,
        )
      : null

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full"
      onClick={(event) => event.stopPropagation()}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label={getLocalizedLabel(
          category.displayNameEn,
          category.displayNameDe,
          currentLanguage,
        )}
        className={classNames(
          "flex h-full min-h-20 w-full flex-wrap content-center items-center gap-1.5 border px-3 py-2 text-left transition-colors",
          isActive
            ? "cursor-text border-yellow1 bg-yellow1/5"
            : "cursor-pointer border-transparent hover:border-grey4 hover:bg-grey1/60",
        )}
        onClick={onActivate}
        onKeyDown={(event) => {
          if ((event.key === "Enter" || event.key === " ") && !isActive) {
            event.preventDefault()
            onActivate()
            return
          }

          if (event.key === "Escape" && isActive) {
            event.preventDefault()
            void onClose()
          }
        }}
      >
        {selectedEntries.map((entry) => (
          <DocumentTagBadge
            key={entry.tag.id}
            entry={entry}
            onRemove={isActive ? () => onRemoveTag(entry) : undefined}
          />
        ))}

        {isActive ? (
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={async (event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault()
                if (optionCount > 0) {
                  setHighlightedIndex((current) =>
                    Math.min(current + 1, optionCount - 1),
                  )
                }
                return
              }

              if (event.key === "ArrowUp") {
                event.preventDefault()
                if (optionCount > 0) {
                  setHighlightedIndex((current) => Math.max(current - 1, 0))
                }
                return
              }

              if (event.key === "Enter") {
                event.preventDefault()
                event.stopPropagation()
                if (filteredTags[highlightedIndex]) {
                  handleSelectTag(filteredTags[highlightedIndex].id)
                  return
                }
                if (
                  canCreateTag &&
                  !isCreatingTag &&
                  highlightedIndex === filteredTags.length
                ) {
                  await handleCreateTag()
                }
                return
              }

              if (event.key === "Escape") {
                event.preventDefault()
                event.stopPropagation()
                void onClose()
              }
            }}
            placeholder={translate("KNOWLEDGEBASE/TAG_ADD_PLACEHOLDER")}
            className="h-6 min-w-24 flex-1 self-center bg-transparent text-sm leading-4 text-grey6 outline-hidden placeholder:text-grey5"
          />
        ) : (
          selectedEntries.length === 0 && (
            <Typography as="span" variant="textXS" className="text-grey5">
              -
            </Typography>
          )
        )}
      </div>
      {dropdown}
    </div>
  )
}
